import { useRef, useEffect, forwardRef, useImperativeHandle, useState } from 'react';
import { loadEmulatorBridge } from '../../utils/emulatorBridgeLoader';
import { generateDefaultManifest } from '../../utils/manifestGenerator';
import { resolveAssetTemplates } from '../../utils/assetTemplateResolver';

interface GamePreviewProps {
  code: string;
  projectId: string | null;
  previewKey: number;
  onBridgeReady: (ready: boolean) => void;
  onConsoleError?: (errors: string[]) => void;
  sdkVersion?: string | null;
  isMuted?: boolean;
}

export interface GamePreviewHandle {
  iframeRef: React.RefObject<HTMLIFrameElement>;
}

const GamePreview = forwardRef<GamePreviewHandle, GamePreviewProps>(
  ({ code, projectId, previewKey, onBridgeReady, onConsoleError, sdkVersion, isMuted = false }, ref) => {
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const errorsRef = useRef<string[]>([]);
    const [resolvedCode, setResolvedCode] = useState<string>(code);

    // Expose iframe ref to parent
    useImperativeHandle(ref, () => ({
      iframeRef
    }));

    // Function to apply mute state to all media elements in iframe
    const applyMuteState = (mutedState: boolean) => {
      if (!iframeRef.current) return;

      try {
        const iframeWindow = iframeRef.current.contentWindow;
        if (!iframeWindow) return;

        const iframeDocument = iframeWindow.document;
        
        // Get all audio and video elements
        const audioElements = iframeDocument.querySelectorAll('audio');
        const videoElements = iframeDocument.querySelectorAll('video');
        
        // Mute/unmute all audio elements
        audioElements.forEach((audio: HTMLAudioElement) => {
          audio.muted = mutedState;
        });
        
        // Mute/unmute all video elements
        videoElements.forEach((video: HTMLVideoElement) => {
          video.muted = mutedState;
        });
      } catch (error) {
        console.error('Failed to apply mute state:', error);
      }
    };

    // Apply mute state whenever it changes
    useEffect(() => {
      applyMuteState(isMuted);
    }, [isMuted]);

    // Resolve asset templates when code changes
    useEffect(() => {
      const resolveAssets = async () => {
        if (!projectId) {
          setResolvedCode(code);
          return;
        }

        try {
          const resolved = await resolveAssetTemplates(code, projectId);
          setResolvedCode(resolved);
        } catch (error) {
          console.error('[GamePreview] Failed to resolve asset templates:', error);
          setResolvedCode(code); // Fallback to original code
        }
      };

      resolveAssets();
    }, [code, projectId]);

    // Load SDK bridge when iframe loads
    useEffect(() => {
      if (!iframeRef.current || !projectId) return;

      const loadBridge = async () => {
        try {
          onBridgeReady(false);

          const manifest = sdkVersion 
            ? generateDefaultManifest(projectId, sdkVersion)
            : generateDefaultManifest(projectId);
          await loadEmulatorBridge(iframeRef.current!, manifest);

          // Apply mute state after bridge is loaded
          applyMuteState(isMuted);

          onBridgeReady(true);
          console.log('✅ SDK Bridge ready');
        } catch (error) {
          console.error('❌ Failed to load SDK bridge:', error);
          onBridgeReady(false);
        }
      };

      const iframe = iframeRef.current;
      iframe.addEventListener('load', loadBridge);

      return () => {
        iframe.removeEventListener('load', loadBridge);
      };
    }, [projectId, previewKey, onBridgeReady]);

    // Monitor console errors in iframe
    useEffect(() => {
      if (!iframeRef.current || !onConsoleError) return;

      const setupConsoleListener = () => {
        try {
          const iframeWindow = iframeRef.current?.contentWindow as Window & { console: Console };
          if (!iframeWindow) return;

          // Clear errors on new game load
          errorsRef.current = [];
          onConsoleError([]);

          // Hook into console.error
          const originalError = iframeWindow.console.error;
          iframeWindow.console.error = (...args: any[]) => {
            // Collect error message and stack trace
            let errorMessage = '';
            let stackTrace = '';

            args.forEach((arg, index) => {
              if (arg instanceof Error || (arg && arg.stack)) {
                // Error object - extract message and stack
                errorMessage += (index > 0 ? ' ' : '') + (arg.message || String(arg));
                stackTrace = arg.stack || '';
              } else if (typeof arg === 'object') {
                try {
                  errorMessage += (index > 0 ? ' ' : '') + JSON.stringify(arg);
                } catch {
                  errorMessage += (index > 0 ? ' ' : '') + String(arg);
                }
              } else {
                errorMessage += (index > 0 ? ' ' : '') + String(arg);
              }
            });

            // Combine message and stack trace
            const fullError = stackTrace 
              ? `${errorMessage}\n\nStack trace:\n${stackTrace}`
              : errorMessage;

            // Add to errors array
            errorsRef.current.push(fullError);
            onConsoleError([...errorsRef.current]);

            console.log('🔴 Game console error detected:', fullError);

            // Call original console.error to preserve browser dev tools
            originalError.apply(iframeWindow.console, args);
          };

          console.log('✅ Console error listener installed');
        } catch (error) {
          console.error('Failed to setup console listener:', error);
        }
      };

      const iframe = iframeRef.current;
      iframe.addEventListener('load', setupConsoleListener);

      return () => {
        iframe.removeEventListener('load', setupConsoleListener);
      };
    }, [previewKey, onConsoleError]);

    return (
      <div className="w-full h-full overflow-hidden">
        <iframe
          ref={iframeRef}
          key={previewKey}
          srcDoc={resolvedCode}
          sandbox="allow-scripts allow-same-origin"
          className="border-0"
          title="Game Preview"
          style={{
            width: '400px',
            height: '80%',
            transform: 'scale(1.25)',
            transformOrigin: '0% 0%'
          }}
        />
      </div>
    );
  }
);

GamePreview.displayName = 'GamePreview';

export default GamePreview;

